"use client"

import { useEffect } from "react"

const BUILD_STORAGE_KEY = "projectpulse.app.buildId"
const BUILD_RELOAD_KEY = "projectpulse.app.buildReloadedFor"
const DETAIL_SESSION_KEYS = ["lastTaskId", "lastBugId", "lastProjectId", "lastMilestoneId"]

type DeploymentMetadata = {
  buildId?: string
  builtAt?: string
}

function clearStaleRuntimeState() {
  try {
    localStorage.removeItem("user")
    localStorage.removeItem("permissions")
    DETAIL_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Storage can be unavailable in restricted browser modes.
  }
}

async function fetchDeploymentMetadata(): Promise<DeploymentMetadata | null> {
  const response = await fetch(`/deployment.json?t=${Date.now()}`, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  })
  if (!response.ok) return null
  return response.json()
}

export default function BuildVersionGuard() {
  useEffect(() => {
    let cancelled = false

    const checkBuildVersion = async () => {
      try {
        const metadata = await fetchDeploymentMetadata()
        if (cancelled) return

        const deployedBuildId = String(metadata?.buildId || "").trim()
        if (!deployedBuildId) return

        const knownBuildId = localStorage.getItem(BUILD_STORAGE_KEY)
        if (!knownBuildId) {
          localStorage.setItem(BUILD_STORAGE_KEY, deployedBuildId)
          return
        }

        if (knownBuildId === deployedBuildId) return

        localStorage.setItem(BUILD_STORAGE_KEY, deployedBuildId)
        clearStaleRuntimeState()

        if (sessionStorage.getItem(BUILD_RELOAD_KEY) === deployedBuildId) return
        sessionStorage.setItem(BUILD_RELOAD_KEY, deployedBuildId)
        window.location.reload()
      } catch {
        // Keep the app usable if the metadata file is unavailable in local/dev environments.
      }
    }

    checkBuildVersion()

    const handleFocus = () => checkBuildVersion()
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkBuildVersion()
      }
    }
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      cancelled = true
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  return null
}
